import { HierarchyObj } from 'components/TimeLine'
import { TFunction } from 'i18next'
import _ from 'lodash'
import {
  Accessory,
  Building,
  ColdCell,
  Fleet,
  Garage,
  Office,
  TestTrack,
} from 'model'
import { IPersonaProps } from 'office-ui-fabric-react'
import * as React from 'react'
import { ReadProvingsForAppletQuery } from './BookingsView.generated'
import { additionalTypes } from './index'
import { StatusItemContainer } from './Styles'

export interface FilterObject {
  groupByDepartment: boolean
  resources: string[]
  resourceCategories: string[]
  persons: IPersonaProps[]
  vehicles: string[]
}

export interface ResourceObject {
  buildings: Building[] | undefined
  garages: Garage[] | undefined
  offices: Office[] | undefined
  testTracks: TestTrack[] | undefined
  accessories: Accessory[] | undefined
  coldCells: ColdCell[] | undefined
  refuelling: any[] | undefined
  fleet: Fleet[] | undefined
}

export interface ViewProps {
  hidden: boolean
}

export const StatusItem: React.FunctionComponent<{
  title: string
  color: string
}> = ({ title, color }) => {
  return (
    <StatusItemContainer color={color}>
      <div />
      <span>{title}</span>
    </StatusItemContainer>
  )
}

export const GenerateResources = (
  data: ReadProvingsForAppletQuery | undefined
): ResourceObject => {
  return {
    buildings:
      data && data.provingGround
        ? _.orderBy(
            data.provingGround.buildings as Building[],
            ['title'],
            ['asc']
          )
        : [],
    garages:
      data && data.provingGround && data.provingGround.garages
        ? _.orderBy(data.provingGround.garages as Garage[], ['title'], ['asc'])
        : [],
    coldCells:
      data && data.provingGround && data.provingGround.coldcells
        ? (data.provingGround.coldcells as ColdCell[])
        : [],
    offices:
      data && data.provingGround && data.provingGround.offices
        ? (data.provingGround.offices as Office[])
        : [],
    testTracks:
      data && data.provingGround && data.provingGround.testTracks
        ? (data.provingGround.testTracks as TestTrack[])
        : [],
    accessories:
      data && data.provingGround && data.provingGround.accessories
        ? (data.provingGround.accessories as Accessory[])
        : [],
    refuelling:
      data && data.provingGround && data.provingGround.refuelling
        ? (data.provingGround.refuelling as any[])
        : [],
    fleet:
      data && data.provingGround && data.provingGround.fleet
        ? (data.provingGround.fleet as Fleet[])
        : [],
  } as ResourceObject
}

const GetSingleHierarchyObj = (
  key: string,
  title: string,
  typename: string,
  childs: any[]
): HierarchyObj => {
  return {
    key,
    value: {
      title,
      __typename: typename,
    },
    children: childs.map((child: any) => {
      return {
        _id: child._id,
        title: child.title,
        __typename: child.__typename,
      }
    }),
  } as HierarchyObj
}

export const GenerateHierarchy = (
  resources: ResourceObject,
  filter: FilterObject,
  t: TFunction
): HierarchyObj[] => {
  const result = [] as HierarchyObj[]

  // Generate Building Hierarchy and group by building and Rooms
  resources.buildings &&
    resources.buildings.forEach((building: Building) => {
      // if this current Building is in resource Selection
      if (filter.resources.includes(building._id)) {
        const officeArray = [] as HierarchyObj[]
        // tslint:disable-next-line: no-unused-expression
        resources.offices &&
          resources.offices.forEach((office: Office) => {
            if (office.parentId === building._id) {
              officeArray.push({
                key: office._id,
                value: office,
                children: office.workstations,
              })
            }
          })
        // push building with offices into main result object
        result.push({
          key: building._id,
          value: building,
          children: officeArray,
        } as HierarchyObj)
      }
    })

  resources.buildings &&
    resources.buildings.forEach((building: Building) => {
      const garageArray = [] as HierarchyObj[]
      // tslint:disable-next-line: no-unused-expression
      resources.garages &&
        resources.garages.forEach((garage: Garage) => {
          if (
            garage.parentId === building._id &&
            filter.resources.includes(garage._id)
          ) {
            if (garage.liftingPlatforms) {
              garageArray.push({
                key: 'notdefined',
                value: garage,
                children: garage.liftingPlatforms,
              } as HierarchyObj)
            }
          }
        })
      if (garageArray.length > 0) {
        result.push({
          key: building._id,
          value: building,
          children: garageArray,
        } as HierarchyObj)
      }
    })

  // check if any type in additional types is presented in the category selection array

  const typesSelected = _.intersectionWith(
    filter.resourceCategories,
    additionalTypes,
    _.isEqual
  )
  if (typesSelected.length > 0) {
    const groupedTypeResources = _(resources.accessories)
      .groupBy((x) => x.type)
      .map((value, key) => ({ id: key, items: value }))
      .value()
    // add additional accessory group and item by its special type
    typesSelected.forEach((type: string) => {
      const typeResult = _.find(groupedTypeResources, ['id', type])
      if (typeResult) {
        result.push(
          GetSingleHierarchyObj(
            type,
            t(`field.type.${type}`),
            'SubCategory',
            typeResult.items
          )
        )
      }
    })
  }

  if (filter.resourceCategories.includes('coldCell') && resources.coldCells) {
    result.push(
      GetSingleHierarchyObj(
        'coldCell',
        t(`field.category.coldCell`),
        'MainCategory',
        resources.coldCells
      )
    )
  }

  if (
    filter.resourceCategories.includes('refuelling') &&
    resources.refuelling
  ) {
    const groupedRefuellingResources = _(resources.refuelling)
      .groupBy((x) => x.type)
      .map((value, key) => ({ id: key, items: value }))
      .value()

    _.forEach(groupedRefuellingResources, (item) => {
      result.push(
        GetSingleHierarchyObj(
          item.id,
          t(`field.type.${item.id}`),
          'MainCategory',
          item.items
        )
      )
    })
  }

  if (filter.resourceCategories.includes('testing')) {
    const testingArray = [] as HierarchyObj[]
    if (resources.testTracks) {
      resources.testTracks.forEach((track: any) => {
        testingArray.push({
          key: track._id,
          value: track,
          children: track.usages,
        } as HierarchyObj)
      })
      if (testingArray.length > 0) {
        result.push({
          key: t(`field.category.testing`),
          value: {
            title: t(`field.category.testing`),
            __typename: 'MainCategory',
          },
          children: testingArray,
        } as HierarchyObj)
      }
    }
  }

  if (filter.resourceCategories.includes('fleet')) {
    const filteredResources =
      resources.fleet && resources.fleet.filter((f) => !f.inactive)
    const groupedFleetResources = _(filteredResources)
      .groupBy((x) => x.type)
      .map((value, key) => ({ id: key, items: value }))
      .value()
    const fleetArray = [] as HierarchyObj[]

    _.forEach(groupedFleetResources, (item) => {
      fleetArray.push(
        GetSingleHierarchyObj(
          item.id,
          t(`field.type.${item.id}`),
          'SubCategory',
          item.items
        )
      )
    })

    if (fleetArray.length > 0) {
      result.push({
        key: t(`field.category.fleet`),
        value: {
          title: t(`field.category.fleet`),
          __typename: 'MainCategory',
        },
        children: fleetArray,
      } as HierarchyObj)
    }
  }

  return result
}
